import { describeApiError, type Room } from '@brush/api-client';
import { ErrorNotice } from '@brush/ui';
import { Award, Plus, Sparkles, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { gamingDayLabel, utcOffsetLabel, visibleRooms, zoneCity } from '../rooms/columns';
import { operatorReadOf, operatorsCell, type OperatorRead } from '../operators/column';
import { usePatchRoom, useRoomOperators } from '../queries';
import { roomDialogState, type RoomDialogTarget } from '../rooms/dialog';
import { FeatureToggle } from './FeatureToggle';
import { RoomModal } from './RoomModal';

export function RoomsTable({ rooms }: { rooms: Room[] }) {
  // AN ID AND A MODE, resolved against the live list below — never the room object. See
  // `rooms/dialog.ts` for the shape this replaced and what it cost.
  const [target, setTarget] = useState<RoomDialogTarget>({ mode: 'closed' });
  // Internal rooms are shown by default: hiding them is a view of the list, and a default that hid
  // rooms would make them look deleted to whoever opened the page next.
  const [showInternal, setShowInternal] = useState(true);

  // WHAT THE DIALOG'S KEY IS, and it is a session rather than the room. A key on the room id would
  // remount the moment a create succeeded and the target became `editing` — discarding, in exactly
  // the case that matters most, every operator staged against a room that had just been made. It
  // still changes between two openings, which is the whole job: the draft must not carry from one
  // room to the next.
  const [session, setSession] = useState(0);

  const open = (next: RoomDialogTarget) => {
    setSession((n) => n + 1);
    setTarget(next);
  };
  const patchRoom = usePatchRoom();
  const busy = patchRoom.isPending;

  // ONE READ FOR EVERY CELL, and it is the read the operators panel already makes — same key, same
  // cache entry, so the column costs no request at all. It is deliberately its own query rather
  // than a field on the room list: `Room` is the staff shape room-web reads too, and hanging
  // colleagues' names off it would ship them to every room console for a column only this one
  // draws. Keeping it separate also keeps the directory off the table's critical path — the
  // endpoint asks Graph for role statuses this column never reads, so a throttled tenant delays
  // these cells and nothing else.
  const operators = useRoomOperators();
  const read: OperatorRead = operatorReadOf(operators);

  const dialog = roomDialogState(target, rooms);

  const { shown, hidden } = visibleRooms(rooms, showInternal);
  // Offsets are now's: computed per render in the browser, never stored, since they move twice a year.
  const now = new Date();

  const toggleStatus = (room: Room) =>
    patchRoom.mutate({
      id: room.id,
      body: { status: room.status === 'active' ? 'inactive' : 'active' },
    });

  return (
    <section className="overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-ink-800/10 px-6 py-4">
        <h2 className="font-display font-semibold text-ink-950">Poker rooms</h2>
        <label className="ml-auto mr-4 flex items-center gap-2 text-sm text-ink-800/70">
          <input
            type="checkbox"
            checked={showInternal}
            onChange={(e) => setShowInternal(e.target.checked)}
          />
          Show internal rooms
          {hidden > 0 ? <span className="text-ink-800/50">({hidden} hidden)</span> : null}
        </label>
        <button
          type="button"
          onClick={() => open({ mode: 'creating' })}
          className="inline-flex items-center gap-2 rounded-lg bg-felt-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-felt-900"
        >
          <Plus className="size-4" /> Register room
        </button>
      </header>

      <ErrorNotice message={describeApiError(patchRoom.error)} className="mx-6 mt-4" />

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-felt-50/50 text-xs uppercase tracking-wide text-ink-800/60">
            <tr>
              <th className="px-6 py-3 font-medium">Room</th>
              <th className="px-6 py-3 font-medium">Location</th>
              <th className="px-6 py-3 font-medium">Time zone</th>
              <th className="px-6 py-3 font-medium">Gaming day</th>
              <th className="px-6 py-3 font-medium">Players</th>
              <th className="px-6 py-3 font-medium">Tournaments</th>
              <th className="px-6 py-3 font-medium">Operators</th>
              <th className="px-6 py-3 font-medium">Features</th>
              <th className="px-6 py-3 font-medium">Status</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-800/5">
            {shown.map((room) => (
              <tr key={room.id} className="hover:bg-felt-50/30">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <span
                      className="flex size-9 items-center justify-center rounded-lg text-lg text-white"
                      style={{ background: room.primaryColor }}
                    >
                      {room.logo}
                    </span>
                    <span className="font-medium text-ink-950">{room.name}</span>
                    {/* The status badge's shape, and a neutral tone: internal is a fact about how
                        the room is counted, not a warning about it. */}
                    {room.isInternal ? (
                      <span className="rounded-md bg-ink-800/5 px-2 py-1 text-xs font-medium text-ink-800/60">
                        Internal
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="px-6 py-4 text-ink-800/70">{room.location}</td>
                <td className="px-6 py-4 text-ink-800/70" title={room.timeZone}>
                  {zoneCity(room.timeZone)}
                  {(() => {
                    const offset = utcOffsetLabel(room.timeZone, now);
                    return offset ? <span className="ml-2 text-xs text-ink-800/50">{offset}</span> : null;
                  })()}
                </td>
                <td className="px-6 py-4 text-ink-800/70">{gamingDayLabel(room.gamingDayStartMinutes)}</td>
                <td className="px-6 py-4 text-ink-950">{room.players.toLocaleString()}</td>
                <td className="px-6 py-4 text-ink-950">{room.tournaments}</td>
                <td className="px-6 py-4">
                  <OperatorsCell read={read} roomId={room.id} />
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <FeatureToggle
                      enabled={room.canLeaderboard}
                      label="Leaderboards"
                      icon={Award}
                      disabled={busy}
                      onClick={() =>
                        patchRoom.mutate({ id: room.id, body: { canLeaderboard: !room.canLeaderboard } })
                      }
                    />
                    <FeatureToggle
                      enabled={room.canFantasy}
                      label="Fantasy"
                      icon={Sparkles}
                      disabled={busy}
                      onClick={() =>
                        patchRoom.mutate({ id: room.id, body: { canFantasy: !room.canFantasy } })
                      }
                    />
                    <FeatureToggle
                      enabled={room.canRatings}
                      label="Ratings"
                      icon={TrendingUp}
                      disabled={busy}
                      onClick={() =>
                        patchRoom.mutate({ id: room.id, body: { canRatings: !room.canRatings } })
                      }
                    />
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span
                    className={
                      room.status === 'active'
                        ? 'rounded-md bg-felt-50 px-2 py-1 text-xs font-medium text-felt-900'
                        : 'rounded-md bg-ink-800/5 px-2 py-1 text-xs font-medium text-ink-800/50'
                    }
                  >
                    {room.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-4">
                    <button
                      type="button"
                      onClick={() => toggleStatus(room)}
                      disabled={busy}
                      className="text-sm font-medium text-felt-700 hover:text-felt-900 disabled:opacity-50"
                    >
                      {room.status === 'active' ? 'Deactivate' : 'Activate'}
                    </button>
                    {/* Deleting lives inside the dialog, not here: a row full of destructive
                        buttons invites the click this codebase makes people type a name to
                        avoid. */}
                    <button
                      type="button"
                      onClick={() => open({ mode: 'editing', id: room.id })}
                      className="text-sm font-medium text-felt-700 underline underline-offset-2 hover:text-felt-900"
                    >
                      Edit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {shown.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-6 py-10 text-center text-sm text-ink-800/60">
                  {rooms.length === 0
                    ? 'No rooms yet. Register your first poker room to get started.'
                    : `Every room is internal and hidden. Tick "Show internal rooms" to see all ${rooms.length}.`}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/*
        A room that has GONE renders nothing, which closes the dialog — and, being its own kind,
        can never be mistaken for `creating`. The stale target left behind is inert: every other
        kind draws nothing, and any click on Edit or Register replaces it.

        The key is the SESSION, not the room — see `open` above for why.
      */}
      {dialog.kind === 'creating' || dialog.kind === 'editing' ? (
        <RoomModal
          key={session}
          room={dialog.kind === 'editing' ? dialog.room : null}
          onCreated={(created) => setTarget({ mode: 'editing', id: created.id })}
          onClose={() => setTarget({ mode: 'closed' })}
        />
      ) : null}
    </section>
  );
}

/**
 * Who runs one room, in a cell.
 *
 * <p>
 * <b>NAMES, AND NOTHING THE DIRECTORY HAS TO ANSWER FOR.</b> A role status belongs where somebody
 * can act on it, which is the dialog; here it would put a Graph read on the critical path of the
 * list. The four states come from {@link operatorsCell}, which is where the reasoning for each
 * lives — in particular why an unanswered read and a failed one may never fall through to "None".
 * </p>
 */
function OperatorsCell({ read, roomId }: { read: OperatorRead; roomId: string }) {
  const cell = operatorsCell(read, roomId);

  if (cell.kind === 'pending') return <span className="text-sm text-ink-800/40">Loading…</span>;

  // Not an ErrorNotice: the row is fine and the table is fine, and a red box in one cell of eight
  // would say the room had a problem rather than the read.
  if (cell.kind === 'unavailable') {
    return <span className="text-sm text-ink-800/40">Couldn’t load</span>;
  }

  // "None" rather than an empty cell or a dash. A blank reads as a column that failed to render,
  // and `—` means "not recorded" everywhere else here — a different and wrong claim about a room
  // whose operator list is genuinely, knowably empty.
  if (cell.kind === 'none') return <span className="text-sm text-ink-800/50">None</span>;

  return (
    <div className="max-w-56 text-sm text-ink-950">
      {cell.names.join(', ')}
      {/* The count is of what is NOT shown, so these two together are the room's whole list — and
          the way to the rest is Edit, which is where anything can be done about it anyway. */}
      {cell.hidden > 0 ? (
        <span className="text-ink-800/50">{`, +${cell.hidden} more`}</span>
      ) : null}
    </div>
  );
}
